import {
  Select,
  PageWrapper,
  formatDataToLabelValue,
  convertEventSelectToTarget,
  FormFeedBack,
  STATUSPAGE,
} from '@repo/ui';
import {
  GetAllCoursesUserUseCaseInterface,
  GetAllGroupsUserUseCaseInterface,
  GetAllOrganizationsUserUseCaseInterface,
} from 'domain/usecases';
import { useFormik } from 'formik';
import { useAppSelector } from 'presentation/config/hooks/useRedux';
import { useEffect } from 'react';
import {
  Col,
  Row,
  Card,
  Input,
  Label,
  Button,
  CardBody,
  CardFooter,
  Form,
  Spinner,
} from 'reactstrap';
import * as Yup from 'yup';
import './styles.scss';

import { TextAreaMessage } from '../components/TextAreaMessage';
import { usePushNotification } from '../hooks/usePushNotification';

import { PROFILE_TYPE } from 'domain/value-objects/profile-type';

interface IProps {
  getAllGroupsUser?: GetAllGroupsUserUseCaseInterface;
  getAllCoursesUser?: GetAllCoursesUserUseCaseInterface;
  getAllOrganizationsUser?: GetAllOrganizationsUserUseCaseInterface;
}

export const PushNotification = ({
  getAllGroupsUser,
  getAllCoursesUser,
  getAllOrganizationsUser,
}: IProps) => {
  const { organizationsUser, loadingOrganizationsUser } = useAppSelector(
    (state) => state.organizations,
  );

  const { coursesUser, loadingCoursesUser } = useAppSelector(
    (state) => state.courses,
  );

  const { groupsUser } = useAppSelector((state) => state.groups);

  const {
    requestPushNotification,
    requestGetAllGroupsUser,
    requestGetAllCoursesUser,
    requestGetAllOrganizationsUser,
    loading,
  } = usePushNotification({
    getAllGroupsUser,
    getAllCoursesUser,
    getAllOrganizationsUser,
  });

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      group: null,
      course: null,
      profile: null,
      entity: null,
      title: null,
      message: null,
    },
    validationSchema: Yup.object().shape({
      course: Yup.object()
        .shape({
          value: Yup.string(),
          label: Yup.string(),
        })
        .nullable(),

      entity: Yup.object()
        .shape({
          value: Yup.string(),
          label: Yup.string(),
        })
        .nullable(),

      group: Yup.array()
        .nullable()
        .of(
          Yup.object().shape({
            label: Yup.string(),
            value: Yup.string(),
          }),
        ),

      title: Yup.string()
        .max(50, 'O campo deve ter no máximo 50 caracteres.')
        .required('Campo é obrigatório.'),
      message: Yup.string().required('Campo é obrigatório.'),

      profile: Yup.array()
        .required()
        .of(
          Yup.object().shape({
            label: Yup.string(),
            value: Yup.string(),
          }),
        )
        .test(
          'is-not-empty',
          'O campo não pode estar vazio.',
          (value) => value && value.length > 0,
        )
        .required('Campo é obrigatório.'),
    }),
    onSubmit: (values) => {
      requestPushNotification({
        entity: values.entity?.value,
        course: values.course?.value,
        groups: values.group?.map((item) => item.value) || [],
        profiles: values.profile?.map((item) => item.value) || [],
        title: values.title,
        content: values.message,
      });
    },
  });

  const profileOptions = [
    { value: PROFILE_TYPE.TEACHER, label: 'Professores' },
    { value: PROFILE_TYPE.PRECEPTOR, label: 'Preceptores' },
    { value: PROFILE_TYPE.STUDENT, label: 'Estudantes' },
  ];

  useEffect(() => {
    requestGetAllOrganizationsUser({
      course: validation.values?.course?.value,
    });
    requestGetAllCoursesUser(validation.values?.entity?.value);
  }, [validation.values?.course?.value]);

  return (
    <PageWrapper title="Enviar Notificação">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="fw-bold">Enviar Push Notification</h4>
        </div>
      </div>

      <Form
        onSubmit={(e) => {
          e.preventDefault();
          validation.handleSubmit();
          return false;
        }}
      >
        <Card className="p-3">
          <Row>
            <Col md={3}>
              <Label htmlFor="course">Organização</Label>
              <Select
                isClearable
                id="entity"
                name="entity"
                placeholder="Todas as Organizações"
                value={validation.values.entity}
                isLoading={loadingOrganizationsUser}
                onChange={(e) => {
                  if (!e) {
                    validation.resetForm();
                  }
                  requestGetAllCoursesUser(e?.value || '');

                  validation.setValues({
                    ...validation.values,
                    group: null,
                  });
                  validation.handleChange(
                    convertEventSelectToTarget('entity', e),
                  );
                }}
                onBlur={validation.handleBlur}
                options={formatDataToLabelValue(
                  organizationsUser as never,
                  'id',
                  'nome',
                )}
              />
            </Col>
            <Col md={3}>
              <Label htmlFor="course">Curso</Label>
              <Select
                isClearable
                id="course"
                name="course"
                placeholder="Todos os cursos"
                onBlur={validation.handleBlur}
                value={validation?.values?.course}
                options={formatDataToLabelValue(
                  coursesUser as never,
                  'id',
                  'nome',
                )}
                onChange={(e) => {
                  requestGetAllGroupsUser({
                    entityId: validation.values.entity?.value,
                    courseId: e?.value,
                  });

                  ({
                    entityId: validation.values.entity?.value,
                    courseId: e?.value,
                  });
                  validation.setValues({
                    ...validation.values,
                    group: null,
                  });
                  validation.handleChange(
                    convertEventSelectToTarget('course', e),
                  );
                }}
                isDisabled={loadingCoursesUser === true}
              />
            </Col>
            <Col md={3}>
              <Label htmlFor="course">Grupo</Label>

              <Select
                isClearable
                id="group"
                isMulti
                name="group"
                placeholder="Todos os grupos"
                onBlur={validation.handleBlur}
                value={validation?.values?.group}
                options={formatDataToLabelValue(
                  groupsUser as never,
                  'id',
                  'nome',
                )}
                onChange={(e) => {
                  validation.handleChange(
                    convertEventSelectToTarget('group', e),
                  );
                }}
                isDisabled={
                  !(validation.values.entity && validation.values.course)
                }
              />
            </Col>
            <Col md={3}>
              <Label htmlFor="course">Perfis</Label>

              <Select
                isClearable
                id="profile"
                name="profile"
                placeholder=" "
                isMulti
                onBlur={validation.handleBlur}
                value={validation?.values?.profile}
                options={profileOptions}
                onChange={(e) => {
                  validation.handleChange(
                    convertEventSelectToTarget('profile', e),
                  );
                }}
                invalid={!!(validation?.touched && validation?.errors?.profile)}
              />
              <FormFeedBack
                error={validation.errors?.profile as any}
                isInvalid={!!(validation.touched && validation.errors?.profile)}
              />
            </Col>
          </Row>
        </Card>

        <CardBody>
          <Card className="">
            <h5 className="fw-bold p-3">Push Notification</h5>
            <CardBody className="mt-0 pt-0">
              <Row>
                <Col md={12}>
                  <Label htmlFor="course">Título</Label>
                  <Input
                    id="title"
                    maxLength={50}
                    type="text"
                    name="title"
                    className="form-control"
                    value={validation.values.title}
                    onBlur={validation.handleBlur}
                    onChange={validation.handleChange}
                    invalid={
                      !!(validation?.touched && validation?.errors?.title)
                    }
                  />

                  <FormFeedBack
                    error={validation.errors?.title as any}
                    isInvalid={
                      !!(validation.touched && validation.errors?.title)
                    }
                  />
                </Col>
                <TextAreaMessage
                  error={validation.errors?.message as string}
                  value={validation.values?.message}
                  handleChange={validation.handleChange}
                />
              </Row>
            </CardBody>

            <CardFooter className="d-flex justify-content-end">
              <Button
                disable={loading === STATUSPAGE.LOADING}
                type="submit"
                color="primary"
              >
                {loading === STATUSPAGE.LOADING ? (
                  <Spinner size="sm">{['Carregando...']}</Spinner>
                ) : (
                  <>Enviar</>
                )}
              </Button>
            </CardFooter>
          </Card>
        </CardBody>
      </Form>
    </PageWrapper>
  );
};
